/*
 * hk-design-help.js
 *
 * HarperKellee "Want something different?" design-help modal. Adapted from
 * Market Dwellings' md-design-help.js for the hk-shopify theme.
 *
 * Two-phase modal shared by the wedding-directional product page and the cart:
 *   offer        — Pitch screen with a CTA that advances to instructions.
 *                  Shown first in product mode.
 *   instructions — Notes textarea + S3 file uploads. Primary CTA submits the
 *                  product form (product mode) or merges via /cart/change.js
 *                  (cart mode). Shown directly in cart mode (no offer panel).
 *
 * Entry points
 *   [data-hk-design-help-open]    Product page: opens at offer phase.
 *   [data-hk-design-help-toggle]  Cart page: opens at instructions phase,
 *                                 pre-hydrated from data-notes / data-uploads.
 *                                 Save merges into the existing line.
 *
 * Properties written by the product-mode submit:
 *   _Design Help        = "requested"   (discriminator)
 *   _Design Notes       = textarea content (omitted when blank)
 *   _Account ID         = md_account_id cookie (omitted when absent)
 *   _Customer Upload N  = S3 URLs (1..5)
 *
 * Design help is an alternative to the structured template inputs, so on submit
 * the per-sign template properties (in [data-hkwd-props]) are cleared and the
 * section's wording validation is bypassed. The selected variant (color) and
 * quantity still ride along.
 *
 * Uploads reuse MD's S3 bucket + Cognito pool (same AWS account); files are
 * namespaced under the hk-design-help/ prefix.
 */
(function (window, document) {
  'use strict';

  // ── S3 upload config (reuses MD's bucket/pool) ──
  var S3_BUCKET    = 'md-customer-uploads';
  var S3_REGION    = 'us-east-2';
  var S3_POOL_ID   = 'us-east-2:32af96c7-3e43-4010-a185-e14c08cb63cf';
  var MAX_FILES    = 5;
  var MAX_BYTES    = 20 * 1024 * 1024;
  var SESSION_PREFIX = 'hk-design-help';

  // ── Lazy-load AWS SDK once per page ───────────────────────
  var awsLoading = null;
  function ensureAwsSDK() {
    if (window.AWS && window.AWS.config && window.AWS.config.credentials) return Promise.resolve();
    if (awsLoading) return awsLoading;
    awsLoading = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = 'https://sdk.amazonaws.com/js/aws-sdk-2.1691.0.min.js';
      s.onload = function () {
        try {
          window.AWS.config.update({
            region: S3_REGION,
            credentials: new window.AWS.CognitoIdentityCredentials({ IdentityPoolId: S3_POOL_ID })
          });
          resolve();
        } catch (e) { reject(e); }
      };
      s.onerror = function () { reject(new Error('Failed to load AWS SDK')); };
      document.head.appendChild(s);
    });
    return awsLoading;
  }

  function sanitizeName(name) { return name.replace(/[^a-zA-Z0-9._-]/g, '_'); }

  function readCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  }

  function fileNameFromUrl(url) {
    try {
      var pathname = new URL(url).pathname;
      return decodeURIComponent(pathname.split('/').pop() || 'file');
    } catch (e) {
      return (url.split('/').pop() || 'file').replace(/^\d+_/, '');
    }
  }

  // ── Modal singleton ───────────────────────────────────────
  var dialogEl, offerPanel, instructionsPanel;
  var notesEl, filesListEl, filesBtnEl, filesBtnTextEl, filesInputEl;
  var saveBtn, saveBtnLabel, saveBtnArrow, saveBtnSpinner;
  var toInstructionsBtn, backBtn, cancelBtn, closeBtn, backdropEl;

  var mode = 'product';          // 'product' | 'cart'
  var phase = 'offer';            // 'offer' | 'instructions'
  var cartContext = null;
  var fileList = [];
  var nextFileId = 0;
  var sessionKey = '';
  var closeTimer = null;

  function newSessionKey() {
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
  }

  function ensureBound() {
    if (dialogEl) return true;
    dialogEl = document.getElementById('hk-design-help-dialog');
    if (!dialogEl) return false;
    if (dialogEl.parentNode !== document.body) document.body.appendChild(dialogEl);

    offerPanel        = dialogEl.querySelector('[data-hk-help-phase-panel="offer"]');
    instructionsPanel = dialogEl.querySelector('[data-hk-help-phase-panel="instructions"]');
    notesEl           = dialogEl.querySelector('[data-hk-help-notes]');
    filesListEl       = dialogEl.querySelector('[data-hk-help-files-list]');
    filesBtnEl        = dialogEl.querySelector('[data-hk-help-files-btn]');
    filesBtnTextEl    = dialogEl.querySelector('[data-hk-help-files-btn-text]');
    filesInputEl      = dialogEl.querySelector('[data-hk-help-files-input]');
    saveBtn           = dialogEl.querySelector('[data-hk-help-save]');
    saveBtnLabel      = dialogEl.querySelector('[data-hk-help-save-label]');
    saveBtnArrow      = dialogEl.querySelector('[data-hk-help-save-arrow]');
    saveBtnSpinner    = dialogEl.querySelector('[data-hk-help-save-spinner]');
    toInstructionsBtn = dialogEl.querySelector('[data-hk-help-to-instructions]');
    backBtn           = dialogEl.querySelector('[data-hk-help-back]');
    cancelBtn         = dialogEl.querySelector('[data-hk-help-cancel]');
    closeBtn          = dialogEl.querySelector('[data-hk-help-close]');
    backdropEl        = dialogEl.querySelector('[data-hk-help-backdrop]');

    if (filesInputEl)      filesInputEl.addEventListener('change', function () { addFiles(this.files); });
    if (closeBtn)          closeBtn.addEventListener('click', closeDialog);
    if (cancelBtn)         cancelBtn.addEventListener('click', closeDialog);
    if (backdropEl)        backdropEl.addEventListener('click', closeDialog);
    if (toInstructionsBtn) toInstructionsBtn.addEventListener('click', goToInstructions);
    if (backBtn)           backBtn.addEventListener('click', handleBack);
    if (notesEl)           notesEl.addEventListener('input', updateSaveBtn);
    if (saveBtn)           saveBtn.addEventListener('click', handleSave);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && dialogEl && !dialogEl.hasAttribute('hidden')) closeDialog();
    });

    return true;
  }

  // ── Phase management ─────────────────────────────────────

  function setPhase(next) {
    phase = next;
    if (!dialogEl) return;
    dialogEl.setAttribute('data-hk-help-phase', next);
    if (offerPanel)        offerPanel.toggleAttribute('hidden', next !== 'offer');
    if (instructionsPanel) instructionsPanel.toggleAttribute('hidden', next !== 'instructions');
    if (backBtn) backBtn.textContent = mode === 'cart' ? 'Cancel' : 'Go back';
    if (next === 'instructions') {
      ensureAwsSDK().catch(function () {});
      setTimeout(function () { if (notesEl) notesEl.focus(); }, 150);
      updateSaveBtn();
    }
  }

  function goToInstructions() { setPhase('instructions'); }

  function handleBack() {
    if (mode === 'cart') { closeDialog(); return; }
    setPhase('offer');
  }

  // ── File list rendering ────────────────────────────────────

  function getLi(id) {
    return filesListEl ? filesListEl.querySelector('[data-hk-help-file-id="' + id + '"]') : null;
  }

  function makeRemoveBtn(id) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'hk-help-files__remove';
    b.setAttribute('aria-label', 'Remove file');
    b.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M3 3l10 10M13 3L3 13"/></svg>';
    b.addEventListener('click', function () { removeFile(id); });
    return b;
  }

  function renderUploading(entry) {
    var li = getLi(entry.id); if (!li) return;
    li.className = 'hk-help-files__item';
    li.innerHTML = '';
    var top = document.createElement('div');
    top.className = 'hk-help-files__top';
    var spinner = document.createElement('span');
    spinner.className = 'hk-help-files__spinner';
    var name = document.createElement('span');
    name.className = 'hk-help-files__name';
    name.title = entry.name;
    name.textContent = entry.name;
    top.appendChild(spinner); top.appendChild(name); top.appendChild(makeRemoveBtn(entry.id));
    li.appendChild(top);
    var prog = document.createElement('div');
    prog.className = 'hk-help-files__progress';
    prog.innerHTML =
      '<div class="hk-help-files__progress-track">' +
        '<div class="hk-help-files__progress-fill" style="width:' + entry.pct + '%"></div>' +
      '</div>' +
      '<span class="hk-help-files__progress-pct">' + entry.pct + '%</span>';
    li.appendChild(prog);
  }

  function renderProgress(entry) {
    var li = getLi(entry.id); if (!li) return;
    var fill = li.querySelector('.hk-help-files__progress-fill');
    var pct  = li.querySelector('.hk-help-files__progress-pct');
    if (fill) fill.style.width = entry.pct + '%';
    if (pct)  pct.textContent  = entry.pct + '%';
  }

  function renderSuccess(entry) {
    var li = getLi(entry.id); if (!li) return;
    li.className = 'hk-help-files__item hk-help-files__item--success';
    li.innerHTML = '';
    var top = document.createElement('div');
    top.className = 'hk-help-files__top';
    var icon = document.createElement('span');
    icon.className = 'hk-help-files__icon';
    icon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 8l3.5 3.5L13 4"/></svg>';
    var name = document.createElement('span');
    name.className = 'hk-help-files__name';
    name.title = entry.name;
    name.textContent = entry.name;
    top.appendChild(icon); top.appendChild(name); top.appendChild(makeRemoveBtn(entry.id));
    li.appendChild(top);
  }

  function renderError(entry) {
    var li = getLi(entry.id); if (!li) return;
    li.className = 'hk-help-files__item hk-help-files__item--error';
    li.innerHTML = '';
    var top = document.createElement('div');
    top.className = 'hk-help-files__top';
    var icon = document.createElement('span');
    icon.className = 'hk-help-files__icon';
    icon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><circle cx="8" cy="8" r="6.5"/><path d="M8 5v3.5M8 11v.5"/></svg>';
    var name = document.createElement('span');
    name.className = 'hk-help-files__name';
    name.title = entry.name;
    name.textContent = entry.name;
    top.appendChild(icon); top.appendChild(name); top.appendChild(makeRemoveBtn(entry.id));
    li.appendChild(top);
    var errRow = document.createElement('div');
    errRow.className = 'hk-help-files__error-row';
    errRow.innerHTML = '<span>Upload failed — </span>';
    if (entry.file) {
      var retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'hk-help-files__retry';
      retry.textContent = 'Try again';
      retry.addEventListener('click', function () {
        entry.status = 'uploading'; entry.pct = 0;
        renderUploading(entry); uploadFile(entry); updateSaveBtn();
      });
      errRow.appendChild(retry);
    }
    li.appendChild(errRow);
  }

  function updateFilesBtn() {
    if (!filesBtnEl || !filesBtnTextEl) return;
    if (fileList.length >= MAX_FILES) {
      filesBtnEl.classList.add('hk-help-files__btn--hidden');
    } else {
      filesBtnEl.classList.remove('hk-help-files__btn--hidden');
      filesBtnTextEl.textContent = fileList.length === 0 ? 'Choose files' : 'Add more files';
    }
  }

  function removeFile(id) {
    for (var i = 0; i < fileList.length; i++) {
      if (fileList[i].id === id) {
        if (fileList[i].abort) { try { fileList[i].abort(); } catch (_) {} }
        fileList.splice(i, 1);
        break;
      }
    }
    var li = getLi(id);
    if (li && li.parentNode) li.parentNode.removeChild(li);
    updateFilesBtn();
    updateSaveBtn();
  }

  function uploadFile(entry) {
    ensureAwsSDK().then(function () {
      var s3 = new window.AWS.S3({ apiVersion: '2006-03-01' });
      var managed = s3.upload({
        Bucket: S3_BUCKET,
        Key: entry.key,
        Body: entry.file,
        ContentType: entry.file.type || 'application/octet-stream'
      });
      entry.abort = function () { managed.abort(); };
      managed.on('httpUploadProgress', function (evt) {
        if (!evt.total) return;
        entry.pct = Math.round((evt.loaded / evt.total) * 100);
        renderProgress(entry);
      });
      managed.promise().then(function (data) {
        entry.status = 'done';
        entry.url = data.Location;
        entry.abort = null;
        renderSuccess(entry);
        updateSaveBtn();
      }).catch(function (err) {
        if (err && (err.code === 'RequestAbortedError' || err.message === 'Request aborted by user')) return;
        console.error('[hk-design-help] S3 upload error:', err);
        entry.status = 'error';
        entry.abort = null;
        renderError(entry);
        updateSaveBtn();
      });
    }).catch(function (err) {
      console.error('[hk-design-help] AWS SDK init failed:', err);
      entry.status = 'error';
      renderError(entry);
      updateSaveBtn();
    });
  }

  function addFiles(files) {
    for (var i = 0; i < files.length; i++) {
      if (fileList.length >= MAX_FILES) break;
      var f = files[i];
      if (f.size > MAX_BYTES) {
        alert(f.name + ' is over the 20 MB limit and was skipped.');
        continue;
      }
      var id = ++nextFileId;
      var key = SESSION_PREFIX + '/' + sessionKey + '/' + id + '_' + sanitizeName(f.name);
      var entry = { id: id, file: f, key: key, status: 'uploading', pct: 0, url: null, name: f.name };
      fileList.push(entry);
      var li = document.createElement('li');
      li.className = 'hk-help-files__item';
      li.setAttribute('data-hk-help-file-id', id);
      filesListEl.appendChild(li);
      renderUploading(entry);
      uploadFile(entry);
    }
    if (filesInputEl) filesInputEl.value = '';
    updateFilesBtn();
    updateSaveBtn();
  }

  function hydrateUploads(urls) {
    if (!urls || !urls.length) return;
    for (var i = 0; i < urls.length; i++) {
      var url = urls[i];
      if (!url) continue;
      var id = ++nextFileId;
      var entry = { id: id, file: null, key: null, status: 'done', pct: 100, url: url, name: fileNameFromUrl(url) };
      fileList.push(entry);
      var li = document.createElement('li');
      li.className = 'hk-help-files__item';
      li.setAttribute('data-hk-help-file-id', id);
      filesListEl.appendChild(li);
      renderSuccess(entry);
    }
    updateFilesBtn();
  }

  function resetState() {
    fileList.forEach(function (e) { if (e.abort) { try { e.abort(); } catch (_) {} } });
    fileList = [];
    nextFileId = 0;
    sessionKey = newSessionKey();
    if (notesEl) notesEl.value = '';
    if (filesListEl) filesListEl.innerHTML = '';
    if (filesInputEl) filesInputEl.value = '';
    if (saveBtn) {
      saveBtn.disabled = false;
      saveBtn.classList.remove('is-saved');
    }
    setSaveLoading(false);
    updateFilesBtn();
  }

  function setSaveLoading(isLoading) {
    if (saveBtnArrow)   saveBtnArrow.style.display   = isLoading ? 'none' : '';
    if (saveBtnSpinner) saveBtnSpinner.style.display = isLoading ? '' : 'none';
  }

  function updateSaveBtn() {
    if (!saveBtn) return;
    var anyUploading = fileList.some(function (e) { return e.status === 'uploading'; });
    saveBtn.disabled = anyUploading;
    if (saveBtnLabel) {
      if (anyUploading) {
        saveBtnLabel.textContent = 'Uploading…';
      } else {
        saveBtnLabel.textContent = mode === 'cart' ? 'Save' : 'Add to cart';
      }
    }
    setSaveLoading(anyUploading);
  }

  // ── Open / close ──────────────────────────────────────────

  function openDialog(nextMode, ctx) {
    if (!ensureBound()) return;
    if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
    mode = nextMode || 'product';
    cartContext = ctx || null;

    resetState();

    if (mode === 'cart' && ctx) {
      if (notesEl) notesEl.value = ctx.notes || '';
      hydrateUploads(ctx.uploads || []);
      setPhase('instructions');
    } else {
      setPhase('offer');
    }

    dialogEl.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(function () { dialogEl.classList.add('is-open'); });
    updateSaveBtn();
  }

  function closeDialog() {
    if (!dialogEl) return;
    dialogEl.classList.remove('is-open');
    document.body.style.overflow = '';
    closeTimer = setTimeout(function () {
      dialogEl.setAttribute('hidden', '');
      closeTimer = null;
    }, 220);
  }

  // ── Save handlers ─────────────────────────────────────────

  function handleSave() {
    if (saveBtn.disabled) return;
    if (fileList.some(function (e) { return e.status === 'uploading'; })) {
      alert('Please wait for files to finish uploading.');
      return;
    }
    if (mode === 'cart') saveCartMode();
    else saveProductMode();
  }

  function saveProductMode() {
    var form = document.querySelector('.hkwd-form');
    if (!form) return;
    var notesValue = (notesEl && notesEl.value || '').trim();
    var doneUrls = fileList.filter(function (e) { return e.status === 'done' && e.url; })
                           .map(function (e) { return e.url; });
    var accountId = readCookie('md_account_id');

    // Design help bypasses the structured template inputs: clear the per-sign
    // properties the section synced, and flag the form so its wording
    // validation is skipped on submit. Variant id + quantity stay intact.
    var propsBox = form.querySelector('[data-hkwd-props]');
    if (propsBox) propsBox.innerHTML = '';
    form.setAttribute('data-hk-design-help-submitting', '1');

    setProp(form, '_Design Help', 'requested');
    setProp(form, '_Design Notes', notesValue);
    setProp(form, '_Account ID', accountId);
    for (var n = 1; n <= MAX_FILES; n++) {
      setProp(form, '_Customer Upload ' + n, doneUrls[n - 1]);
    }

    saveBtn.disabled = true;
    if (saveBtnLabel) saveBtnLabel.textContent = 'Submitting…';
    setSaveLoading(true);
    form.submit();
  }

  function saveCartMode() {
    if (!cartContext || !cartContext.lineKey) return;
    var lineKey = cartContext.lineKey;
    var notesValue = (notesEl && notesEl.value || '').trim();
    var doneUrls = fileList.filter(function (e) { return e.status === 'done' && e.url; })
                           .map(function (e) { return e.url; });

    saveBtn.disabled = true;
    if (saveBtnLabel) saveBtnLabel.textContent = 'Saving…';
    setSaveLoading(true);

    var capturedVariantId = null;
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var item = (cart.items || []).find(function (i) { return i.key === lineKey; });
        if (!item) throw new Error('line not found: ' + lineKey);
        capturedVariantId = item.variant_id;
        // /cart/change.js REPLACES the line's properties, so merge existing,
        // override notes + each upload slot, then strip blanks (removes any
        // notes / uploads the customer just cleared).
        var props = Object.assign({}, item.properties, { '_Design Notes': notesValue });
        for (var n = 1; n <= MAX_FILES; n++) {
          props['_Customer Upload ' + n] = doneUrls[n - 1];
        }
        Object.keys(props).forEach(function (k) {
          if (isBlank(props[k])) delete props[k];
        });
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: lineKey, properties: props })
        });
      })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, cart: j }; }); })
      .then(function (res) {
        if (!res.ok) throw new Error('cart/change.js failed');
        var toggle = document.querySelector('[data-hk-design-help-toggle][data-line-key="' + lineKey + '"]');
        if (toggle && capturedVariantId) {
          var updated = (res.cart.items || []).find(function (i) { return i.variant_id === capturedVariantId; });
          if (updated) toggle.dataset.lineKey = updated.key;
          toggle.dataset.notes = notesValue;
          toggle.dataset.uploads = doneUrls.join('|||');
          toggle.textContent = (notesValue || doneUrls.length) ? 'View / edit' : 'Add details';
        }
        if (saveBtnLabel) saveBtnLabel.textContent = 'Saved';
        saveBtn.classList.add('is-saved');
        setTimeout(closeDialog, 700);
      })
      .catch(function (err) {
        console.error('[hk-design-help] cart save failed:', err);
        saveBtn.disabled = false;
        if (saveBtnLabel) saveBtnLabel.textContent = 'Save';
        setSaveLoading(false);
      });
  }

  function isBlank(v) {
    return v === null || v === undefined || (typeof v === 'string' && v.trim() === '');
  }

  // Set or create a hidden input named properties[<key>] on the form.
  // Blank values remove any pre-existing input instead of writing "".
  function setProp(form, key, value) {
    var name = 'properties[' + key + ']';
    var input = form.querySelector('input[name="' + name + '"]');
    if (isBlank(value)) {
      if (input && input.parentNode) input.parentNode.removeChild(input);
      return;
    }
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  // ── Triggers ──────────────────────────────────────────────

  document.addEventListener('click', function (e) {
    var openBtn = e.target.closest('[data-hk-design-help-open]');
    if (openBtn) { e.preventDefault(); openDialog('product', null); return; }
    var toggle = e.target.closest('[data-hk-design-help-toggle]');
    if (toggle) {
      e.preventDefault();
      var raw = toggle.dataset.uploads || '';
      var uploads = raw ? raw.split('|||').filter(Boolean) : [];
      openDialog('cart', {
        lineKey: toggle.dataset.lineKey,
        notes: toggle.dataset.notes || '',
        uploads: uploads
      });
    }
  });

  window.HKDesignHelpDialog = {
    open: openDialog,
    close: closeDialog
  };
})(window, document);
